import type { StateCreator } from "zustand"
import { create } from "zustand"
import { persist } from "zustand/middleware"

const COMPANIES_SORT_TYPES = [
	"name-asc",
	"name-desc",
	"newest",
	"oldest",
	"mrr-asc",
	"mrr-desc",
	"size-asc",
	"size-desc",
	"tier-asc",
	"tier-desc",
	"stage-asc",
	"stage-desc",
] as const

export type CompaniesSortType = (typeof COMPANIES_SORT_TYPES)[number]

export const DEFAULT_COMPANIES_SORT_TYPE: CompaniesSortType = "name-asc"

export const isCompaniesSortType = (
	value: unknown,
): value is CompaniesSortType =>
	typeof value === "string" &&
	(COMPANIES_SORT_TYPES as readonly string[]).includes(value)

type CompaniesSortColumn =
	| "name"
	| "createdAt"
	| "revenue"
	| "size"
	| "tierName"
	| "statusName"

export const resolveCompaniesSort = (
	sortType: CompaniesSortType,
): { sortColumn: CompaniesSortColumn; sortOrder: "asc" | "desc" } => {
	switch (sortType) {
		case "name-asc":
			return { sortColumn: "name", sortOrder: "asc" }
		case "name-desc":
			return { sortColumn: "name", sortOrder: "desc" }
		case "newest":
			return { sortColumn: "createdAt", sortOrder: "desc" }
		case "oldest":
			return { sortColumn: "createdAt", sortOrder: "asc" }
		case "mrr-asc":
			return { sortColumn: "revenue", sortOrder: "asc" }
		case "mrr-desc":
			return { sortColumn: "revenue", sortOrder: "desc" }
		case "size-asc":
			return { sortColumn: "size", sortOrder: "asc" }
		case "size-desc":
			return { sortColumn: "size", sortOrder: "desc" }
		case "tier-asc":
			return { sortColumn: "tierName", sortOrder: "asc" }
		case "tier-desc":
			return { sortColumn: "tierName", sortOrder: "desc" }
		case "stage-asc":
			return { sortColumn: "statusName", sortOrder: "asc" }
		case "stage-desc":
			return { sortColumn: "statusName", sortOrder: "desc" }
		default:
			return { sortColumn: "name", sortOrder: "asc" }
	}
}

interface CompaniesSortState {
	sortType: CompaniesSortType
	setSortType: (type: CompaniesSortType) => void
}

const filterDefinition: StateCreator<CompaniesSortState> = (set) => ({
	sortType: DEFAULT_COMPANIES_SORT_TYPE,
	setSortType: (type) => {
		set({ sortType: type })
	},
})

export const mergeCompaniesSortState = (
	persistedState: unknown,
	currentState: CompaniesSortState,
): CompaniesSortState => {
	const persisted = persistedState as Partial<CompaniesSortState> | undefined
	return {
		...currentState,
		...persisted,
		sortType: isCompaniesSortType(persisted?.sortType)
			? persisted.sortType
			: DEFAULT_COMPANIES_SORT_TYPE,
	}
}

export const useCompaniesSortState = create<CompaniesSortState>()(
	persist(filterDefinition, {
		name: "companies-sort-state",
		merge: mergeCompaniesSortState,
	}),
)
